import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  loginSchema,
  type LoginInput,
  resolveErrorMessage,
  type ResolvedError,
} from '@kidshome/api-contract';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '@kidshome/ui';
import { useAuthActions, onLoggedIn, useAuthStore } from '@/entities/session';
import { PasswordInput } from '@/shared/ui';
import { AuthPageShell } from '@/shared/ui/AuthPageShell';
import { tDynamic } from '@/shared/lib/i18n';
import { loginDefaults } from '@/shared/lib/dev-login';

/** Sign-in coordinates another screen can hand to `/login` via navigation state (e.g. the
 * accept-invite success step deep-links here with the email it just returned, or a completed
 * household erasure hands off `deleted` so the calm goodbye can render above the form). */
interface LoginPrefill {
  email?: string;
  deleted?: boolean;
}

/**
 * Login screen (FSD `pages`). RHF + Zod, reusing the shared `loginSchema` from api-contract (same
 * rules the server enforces). The email is the global login id and resolves the household
 * server-side (ADR-0071) — no household id is ever entered. Field errors and the submit error are
 * i18n keys resolved via `t`. No hardcoded user-facing strings (ADR-0025).
 *
 * The email prefills from, in order: navigation state (accept-invite hand-off), then the dev seed.
 *
 * Also the post-erasure landing page (M7 35b, ADR-0072): `useDeleteHousehold` redirects here with
 * `state: { deleted: true }` once a family account is gone, so a calm goodbye banner renders above
 * the form (`admin:privacy.goodbye.*` — this used to live on the now-removed `/register` page).
 */
export function LoginPage(): React.ReactElement {
  const { t } = useTranslation(['auth', 'common', 'settings', 'validation', 'errors', 'admin']);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuthActions();
  const [submitError, setSubmitError] = useState<ResolvedError | null>(null);

  // Dev-only one-click prefill (empty in production builds — see loginDefaults).
  const dev = loginDefaults();
  const prefill = (location.state as LoginPrefill | null) ?? {};
  const deleted = prefill.deleted === true;
  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: prefill.email ?? dev.email,
      password: dev.password,
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      await login(values);
      onLoggedIn();
      // A super-admin (ADR-0072) has no household — land them on the platform console, not the app.
      const me = useAuthStore.getState().user;
      // Resume the deep link that bounced here (protectedLoader stashes it in `?from=`), so a shared or
      // bookmarked link like /calendar or /family/:id survives the login round-trip. Platform admins
      // always land on their console (their `from` would be a household route they can't open). Only
      // honour a LOCAL path (leading single slash) — never an absolute/protocol-relative open redirect.
      const from = new URLSearchParams(location.search).get('from');
      const safeFrom = from && from.startsWith('/') && !from.startsWith('//') ? from : null;
      const target = me?.kind === 'platform' ? '/platform' : (safeFrom ?? '/');
      void navigate(target, { replace: true });
    } catch (error) {
      setSubmitError(resolveErrorMessage(error));
    }
  });

  return (
    <AuthPageShell>
      {deleted && (
        <div
          role="status"
          className="w-full max-w-sm rounded-card border border-border bg-surface-muted p-4 text-center"
        >
          <p className="font-display text-base font-bold text-text">
            {t('admin:privacy.goodbye.title')}
          </p>
          <p className="mt-1 text-sm text-text-muted">{t('admin:privacy.goodbye.body')}</p>
        </div>
      )}
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>{t('auth:login.title')}</CardTitle>
          <CardDescription>{t('auth:login.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="email">{t('auth:login.email')}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                aria-invalid={!!form.formState.errors.email}
                aria-describedby={form.formState.errors.email ? 'email-error' : undefined}
                {...form.register('email')}
              />
              {form.formState.errors.email?.message && (
                <p id="email-error" role="alert" className="text-sm text-danger">
                  {tDynamic(t, form.formState.errors.email.message)}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="password">{t('auth:login.password')}</Label>
              <PasswordInput
                id="password"
                autoComplete="current-password"
                aria-invalid={!!form.formState.errors.password}
                aria-describedby={form.formState.errors.password ? 'password-error' : undefined}
                {...form.register('password')}
              />
              {form.formState.errors.password?.message && (
                <p id="password-error" role="alert" className="text-sm text-danger">
                  {tDynamic(t, form.formState.errors.password.message)}
                </p>
              )}
            </div>

            {submitError && (
              <p role="alert" className="text-sm text-danger">
                {tDynamic(t, submitError.key, submitError.params)}
              </p>
            )}

            <Button type="submit" disabled={form.formState.isSubmitting}>
              {t('auth:login.submit')}
            </Button>
          </form>

          <div className="mt-4 flex flex-col gap-2 text-center">
            <Link
              to="/forgot-password"
              className="text-sm font-medium text-primary underline-offset-4 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t('auth:forgotPassword.link')}
            </Link>
            <Link
              to="/kids"
              className="text-sm font-medium text-primary underline-offset-4 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t('auth:login.childLogin')}
            </Link>
          </div>
        </CardContent>
      </Card>
    </AuthPageShell>
  );
}
